import * as errors from '@attain-apps/errors'
import { TypeChecker } from '@attain-apps/type-guards'

export class Range<in out T extends NonNullable<unknown>> {
  // TODO: missing js doc
  public static everything<T extends NonNullable<unknown>>(): Range<T> {
    return new Range({ includesEnd: true, includesStart: true })
  }
  public static empty<T extends NonNullable<unknown>>(): Range<T> {
    return new Range()
  }

  /**
   * Tries to create a {@link Range} by parsing a string value in the format of
   * a postgres range.
   *
   * TODO we need to be able to parse Date Multiranges (and other Multiranges)
   * so that they have the right values (e.g. a javascript Date object)
   */
  public static fromPostgres<T extends NonNullable<unknown>>(
    pgStr: string,
    parseValue: (val: string) => T,
  ): Range<T>
  public static fromPostgres(pgStr: string): Range<string>
  public static fromPostgres(
    pgStr: string,
    parseValue?: (val: string) => NonNullable<unknown>,
  ): Range<NonNullable<unknown>>
  public static fromPostgres(
    pgStr: string,
    parseValue?: (val: string) => NonNullable<unknown>,
  ): Range<any> {
    pgStr = pgStr.trim()
    if (pgStr === 'empty') {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return Range.empty()
    }
    // Regex that splits range into brackets and start and end of a range
    const regex =
      /(?<startBracket>\[|\()(?<start>.*?),(?<end>.*?)(?<endBracket>\]|\))/
    const parts = regex.exec(pgStr)?.groups
    if (!parts) {
      throw new errors.ValidationError({
        type: 'range format',
        value: pgStr,
      })
    }

    const { startBracket, endBracket } = parts
    let { start, end } = parts
    start = start?.trim() ?? ''
    end = end?.trim() ?? ''

    return new Range({
      end: end === '' ? undefined : parseValue ? parseValue(end) : end,
      includesEnd: endBracket === ']',
      includesStart: startBracket === '[',
      start: start === '' ? undefined : parseValue ? parseValue(start) : start,
    })
  }

  /**
   * Lower endpoint of the range. Undefined is the lowest possible value
   */
  public readonly start?: T
  /**
   * Upper endpoint of the range. Undefined is the highest possible value
   */
  public readonly end?: T
  public readonly includesStart: boolean
  public readonly includesEnd: boolean
  public readonly isEmpty: boolean

  /**
   * Range includes everything below the upper endpoint
   */
  public get hasNoStart(): boolean {
    return this.start === undefined
  }
  /**
   * Range includes everything above the lower endpoint
   */
  public get hasNoEnd(): boolean {
    return this.end === undefined
  }
  /**
   * Range includes everything
   */
  public get includesEverything(): boolean {
    return this.hasNoStart && this.hasNoEnd
  }

  constructor(start: T, end: T)
  constructor(source?: Range.constructor.Params<T> | Range<T>)
  constructor(
    firstParam?: T | Range<T> | Range.constructor.Params<T>,
    end?: T,
  ) {
    const params: Range.constructor.Params<T> =
      end === undefined
        ? (firstParam as Range.constructor.Params<T>)
        : {
            end,
            start: firstParam as T,
          }
    // If no params are passed, or if start is after end, the range is empty
    if (
      !params ||
      (params.start === undefined && params.end === undefined) ||
      (params.start !== undefined &&
        params.end !== undefined &&
        params.start > params.end)
    ) {
      this.isEmpty = true
      this.includesStart = this.includesEnd = false
      return
    }
    this.start = params.start
    this.end = params.end
    // If there is no start, then we set this to false, just for consistency
    this.includesStart =
      this.start === undefined ? false : (params.includesStart ?? true)
    // If there is no end, then we set this to false, just for consistency
    this.includesEnd =
      this.end === undefined ? false : (params.includesEnd ?? true)
    this.isEmpty =
      params.start === params.end &&
      params.start !== undefined &&
      !this.includesStart &&
      !this.includesEnd
  }

  public contains(val: T | Range<T>): boolean {
    if (this.isEmpty) {
      // NOTE: we attempt to match Postgres' handling of empty ranges
      return false
    }
    let valStart: T | undefined, valEnd: T | undefined
    if (val instanceof Range) {
      if (val.isEmpty) {
        // NOTE: we attempt to match Postgres' handling of empty ranges
        return true
      }
      valStart = val.start
      valEnd = val.end
    } else {
      valStart = valEnd = val
    }
    return (
      _compare(
        this.start ?? -Infinity,
        valStart ?? -Infinity,
        this.includesStart,
      ) && _compare(valEnd ?? Infinity, this.end ?? Infinity, this.includesEnd)
    )
  }
  public overlaps(range: Range<T>): boolean {
    // NOTE: we attempt to match Postgres' handling of empty ranges
    return (
      !this.isEmpty &&
      !range.isEmpty &&
      !this.isStrictlyLeftOf(range) &&
      !this.isStrictlyRightOf(range)
    )
  }
  public isStrictlyLeftOf(range: Range<T>): boolean {
    // NOTE: we attempt to match Postgres' handling of empty ranges
    return (
      !this.isEmpty &&
      !range.isEmpty &&
      _compare(
        this.end ?? Infinity,
        range.start ?? -Infinity,
        !this.includesEnd || !range.includesStart,
      )
    )
  }
  public isStrictlyRightOf(range: Range<T>): boolean {
    return range.isStrictlyLeftOf(this)
  }

  public union(range: Range<T>): Range<T> {
    if (this.isEmpty) {
      return range
    }
    if (range.isEmpty) {
      return this
    }
    const end =
      this.end === undefined || range.end === undefined
        ? undefined
        : this.end < range.end
          ? range.end
          : this.end
    const start =
      this.start === undefined || range.start === undefined
        ? undefined
        : this.start > range.start
          ? range.start
          : this.start
    return new Range({
      end,
      includesEnd:
        (end === this.end && this.includesEnd) ||
        (end === range.end && range.includesEnd),
      includesStart:
        (start === this.start && this.includesStart) ||
        (start === range.start && range.includesStart),
      start,
    })
  }
  public intersection(range: Range<T>): Range<T> {
    if (this.isEmpty || range.isEmpty) {
      return Range.empty()
    }
    const end =
      this.end === undefined
        ? range.end
        : range.end === undefined
          ? this.end
          : this.end < range.end
            ? this.end
            : range.end
    const start =
      this.start === undefined
        ? range.start
        : range.start === undefined
          ? this.start
          : this.start > range.start
            ? this.start
            : range.start
    if (start !== undefined && end !== undefined && start > end) {
      Range.empty()
    }
    return new Range({
      end,
      includesEnd: !(
        (end === this.end && !this.includesEnd) ||
        (end === range.end && !range.includesEnd)
      ),
      includesStart: !(
        (start === this.start && !this.includesStart) ||
        (start === range.start && !range.includesStart)
      ),
      start,
    })
  }

  public toJSON(): Range.JSON<T> {
    return {
      $range: {
        end: this.end,
        includesEnd: this.includesEnd,
        includesStart: this.includesStart,
        start: this.start,
      },
    }
  }

  public toPostgres(endpointSerializer?: (val: T) => string): string {
    if (this.isEmpty) {
      return 'empty'
    }
    const serialize = (val: T | undefined): string =>
      val === undefined
        ? ''
        : endpointSerializer
          ? endpointSerializer(val)
          : TypeChecker.isDate(val)
            ? // check if its valid date
              !isNaN(val.valueOf())
              ? // if its valid, return an iso string representation of it
                val.toISOString()
              : // return empty string for invalid dates to avoid crashing on date.toISOString()
                ''
            : val.toString()
    return [
      this.includesStart ? '[' : '(',
      serialize(this.start),
      ',',
      serialize(this.end),
      this.includesEnd ? ']' : ')',
    ].join('')
  }

  /** Number representing the start value */
  public valueOf(): number {
    if (this.start === undefined) {
      return -Infinity
    }
    const num = Number(this.start.valueOf())
    return isNaN(num) ? Infinity : num
  }
}
export namespace Range {
  export const isRange: TypeChecker<Range<NonNullable<unknown>>> =
    TypeChecker.Factory.instance(Range)

  export namespace constructor {
    export interface Params<T extends NonNullable<unknown>> {
      start?: T
      end?: T
      /** @default true */
      includesStart?: boolean
      /** @default true */
      includesEnd?: boolean
    }
    export namespace Params {
      export const isParams: TypeChecker<Params<any>> =
        TypeChecker.Factory.object({
          end: TypeChecker.isUnknown,
          includesEnd: TypeChecker.Factory.optional(TypeChecker.isBoolean),
          includesStart: TypeChecker.Factory.optional(TypeChecker.isBoolean),
          start: TypeChecker.isUnknown,
        })
    }
  }

  export interface JSON<T = unknown> {
    $range: {
      start?: T
      end?: T
      includesStart: boolean
      includesEnd: boolean
    }
  }

  export function getTypeChecker<T extends NonNullable<unknown>>(
    isT: TypeChecker<T>,
  ): TypeChecker<Range<T>> {
    return TypeChecker.Factory.custom(
      (val: unknown): val is Range<T> =>
        // TODO: Make sure that this logic is correct
        isRange(val) &&
        (val.start === undefined || isT(val.start)) &&
        (val.end === undefined || isT(val.end)),
      {
        get typeDescription() {
          return `Range<${isT.typeDescription}>`
        },
      },
    )
  }
}

function _compare<T extends NonNullable<unknown>>(
  lower: T | number,
  upper: T | number,
  allowEqual = false,
): boolean {
  return (
    lower === -Infinity ||
    upper === Infinity ||
    (allowEqual ? lower <= upper : lower < upper)
  )
}
